import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Cabin } from "../data/mock-flights";

export type TravelProduct = "stays" | "flights";

export type MarketplaceSearchState = {
  product: TravelProduct;
  destination: string;
  checkIn: string;
  checkOut: string;
  guests: number;
  rooms: number;
  origin: string;
  flightTo: string;
  departDate: string;
  returnDate: string;
  tripType: "round" | "oneway";
  passengers: number;
  cabin: Cabin;
};

type SearchContextValue = {
  search: MarketplaceSearchState;
  setSearch: (next: Partial<MarketplaceSearchState>) => void;
  resetSearch: () => void;
};

function isoOffset(days: number) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

const defaultSearch = (): MarketplaceSearchState => {
  const checkIn = isoOffset(7);
  const checkOut = isoOffset(9);
  return {
    product: "stays",
    destination: "",
    checkIn,
    checkOut,
    guests: 2,
    rooms: 1,
    origin: "",
    flightTo: "",
    departDate: checkIn,
    returnDate: checkOut,
    tripType: "round",
    passengers: 1,
    cabin: "Economy",
  };
};

const SearchContext = createContext<SearchContextValue | undefined>(undefined);

export function MarketplaceSearchProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [search, setSearchState] =
    useState<MarketplaceSearchState>(defaultSearch);

  const value = useMemo<SearchContextValue>(
    () => ({
      search,
      setSearch: (next) => setSearchState((prev) => ({ ...prev, ...next })),
      resetSearch: () => setSearchState(defaultSearch()),
    }),
    [search],
  );

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
}

export function useMarketplaceSearch() {
  const ctx = useContext(SearchContext);
  if (!ctx) {
    throw new Error(
      "useMarketplaceSearch must be used within MarketplaceSearchProvider",
    );
  }
  return ctx;
}
